import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { getPricingPackageName } from '../../i18n/helpers';
import '../../App.css';
import './PaymentSuccess.css';
import PaymentScreenWrapper from './PaymentScreenWrapper';

interface PaymentSuccessProps {
  sessionId?: string;
  onGoToRoom?: (roomName: string) => void;
  onGoHome?: () => void;
}

function formatAccessDuration(duration: string): string {
  if (duration === '48h') return '2 days';
  return duration;
}

const PaymentSuccess: React.FC<PaymentSuccessProps> = ({ sessionId: propSessionId, onGoToRoom, onGoHome }) => {
  const { t } = useTranslation();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [roomName, setRoomName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [packageInfo, setPackageInfo] = useState<{
    name: string;
    duration: string;
  } | null>(null);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  useEffect(() => {
    // Get session_id from URL params or props
    const urlParams = new URLSearchParams(window.location.search);
    const sessionIdParam = propSessionId || urlParams.get('session_id');
    
    if (!sessionIdParam) {
      setError(t('payment.success.noSessionId'));
      setLoading(false);
      return;
    }

    setSessionId(sessionIdParam);
    verifyPayment(sessionIdParam);
  }, [propSessionId]);

  const verifyPayment = async (sessionId: string) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const response = await fetch(`${backendUrl}/api/stripe/verify-payment?session_id=${sessionId}`);
      
      if (!response.ok) {
        throw new Error('Failed to verify payment');
      }

      const data = await response.json();
      setRoomName(data.roomName);
      setPackageInfo({
        name: data.packageName || (data.packageId ? getPricingPackageName(data.packageId) : t('payment.success.defaultPackageName')),
        duration: data.duration || 'N/A',
      });
    } catch (err) {
      console.error('Error verifying payment:', err);
      setError(t('payment.success.verifyFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoToRoom = () => {
    if (roomName) {
      // Navigate to host route for premium room
      window.location.href = `/?host=${roomName}`;
    }
  };

  const handleShowEmailInput = () => {
    setShowEmailInput(true);

    if (!roomName) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    fetch(`${backendUrl}/api/stripe/notify-room-email-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roomName, sessionId }),
    }).catch((err) => {
      console.warn('Failed to notify email intent:', err);
    });
  };

  const handleSendEmail = async () => {
    if (!email || !roomName) return;
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError(t('payment.success.emailInvalid'));
      return;
    }

    setEmailSending(true);
    setEmailError(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const response = await fetch(`${backendUrl}/api/stripe/send-room-code-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          roomName,
          sessionId: sessionId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to send email' }));
        throw new Error(errorData.error || 'Failed to send email');
      }

      setEmailSent(true);
      setShowEmailInput(false);
      setEmail('');
    } catch (err) {
      console.error('Error sending email:', err);
      setEmailError(err instanceof Error ? err.message : 'Failed to send email. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };


  if (loading) {
    return (
      <PaymentScreenWrapper>
        <div className="payment-success-container" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>⏳</div>
          <h2 style={{ marginBottom: '1rem', fontSize: 'clamp(1.25rem, 3vw, 1.75rem)' }}>{t('payment.success.verifying')}</h2>
        </div>
      </PaymentScreenWrapper>
    );
  }

  if (error) {
    return (
      <PaymentScreenWrapper>
        <div className="payment-success-container" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>❌</div>
          <h2 style={{ marginBottom: '1rem', fontSize: 'clamp(1.25rem, 3vw, 1.75rem)' }}>{t('payment.success.verificationFailed')}</h2>
          <p style={{ marginBottom: '2rem', fontSize: '0.95rem' }}>{error}</p>
          <button
            className="button"
            onClick={() => {
              if (onGoHome) {
                onGoHome();
              } else {
                // Clear URL params and reload to go to home
                window.history.pushState({}, '', '/');
                window.location.reload();
              }
            }}
            style={{ marginTop: '1rem', fontSize: '0.9rem', padding: '0.75rem 1.25rem' }}
          >
            {t('payment.success.goHome')}
          </button>
        </div>
      </PaymentScreenWrapper>
    );
  }

  return (
    <PaymentScreenWrapper>
      <div className="payment-success-container">
        <div className="payment-success-grid">
          {/* Left Column - Main Content */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '1.5rem',
            gridColumn: 'span 1',
          }}>
            <h1 style={{ 
              marginBottom: '0.5rem', 
              fontSize: 'clamp(1.5rem, 4vw, 2rem)',
              textAlign: 'center',
            }}>
              {t('payment.success.title')}
            </h1>

            <p style={{
              fontSize: 'clamp(0.95rem, 2vw, 1.05rem)',
              textAlign: 'center',
              lineHeight: 1.5,
              margin: 0,
            }}>
              {t('payment.success.subtitle', {
                durationSuffix: packageInfo
                  ? t('payment.success.durationSuffix', { duration: formatAccessDuration(packageInfo.duration) })
                  : '',
              })}
            </p>
            
            {packageInfo && (
              <div className="payment-success-card">
                <h3 style={{ marginBottom: '0.5rem', fontSize: '1.2rem' }}>
                  {packageInfo.name}
                </h3>
                <p style={{ marginBottom: '0', fontSize: '0.95rem' }}>
                  {t('payment.success.accessLabel', { duration: packageInfo.duration })}
                </p>
              </div>
            )}

            {roomName && (
              <div className="payment-success-card payment-success-card--success">
                <h3 style={{ marginBottom: '0.75rem', fontSize: '1.1rem', textAlign: 'center' }}>
                  {t('payment.success.roomCodeTitle')}
                </h3>
                <div className="payment-success-room-code" style={{
                  fontSize: 'clamp(2rem, 5vw, 2.5rem)',
                  fontWeight: 'bold',
                  letterSpacing: '0.3rem',
                  marginBottom: '1rem',
                  fontFamily: 'monospace',
                  textAlign: 'center',
                }}>
                  {roomName}
                </div>
                <p style={{ fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>
                  {t('payment.success.shareCode')}
                </p>

                {/* Email code section */}
                <div className="payment-success-email-card">
                  {emailSent ? (
                    <div className="payment-success-email-sent">
                      {t('payment.success.emailSent')}
                    </div>
                  ) : (
                    <>
                      {!showEmailInput ? (
                        <div style={{ textAlign: 'center' }}>
                          <p style={{
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            marginBottom: '0.5rem',
                            color: 'var(--text-primary)',
                          }}>
                            {t('payment.success.emailTitle')}
                          </p>
                          <p style={{
                            fontSize: '0.85rem',
                            marginBottom: '1rem',
                            lineHeight: 1.5,
                          }}>
                            {t('payment.success.emailDescription', {
                              durationSuffix: packageInfo
                                ? t('payment.success.durationSuffix', { duration: formatAccessDuration(packageInfo.duration) })
                                : '',
                            })}
                          </p>
                          <button
                            className="button"
                            onClick={handleShowEmailInput}
                            style={{
                              fontSize: '0.95rem',
                              padding: '0.75rem 1.5rem',
                              fontWeight: '600',
                            }}
                          >
                            {t('payment.success.emailButton')}
                          </button>
                        </div>
                      ) : (
                        <div className="payment-success-email-form">
                          <label className="form-label" style={{
                            marginBottom: '0.5rem',
                            textAlign: 'left',
                          }}>
                            {t('payment.success.emailLabel')}
                          </label>
                          <input
                            type="email"
                            className="input"
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              setEmailError(null);
                            }}
                            placeholder={t('payment.success.emailPlaceholder')}
                            style={{
                              marginBottom: '0.5rem',
                              fontSize: '0.9rem',
                              padding: '0.7rem',
                            }}
                            disabled={emailSending}
                          />
                          {emailError && (
                            <p style={{ color: 'var(--color-error)', fontSize: '0.8rem', marginBottom: '0.5rem', textAlign: 'left' }}>
                              {emailError}
                            </p>
                          )}
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                            <button
                              className="button"
                              onClick={handleSendEmail}
                              disabled={emailSending || !email}
                              style={{
                                flex: 1,
                                fontSize: '0.9rem',
                                padding: '0.6rem 0.9rem',
                                opacity: (emailSending || !email) ? 0.6 : 1,
                                cursor: (emailSending || !email) ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {emailSending ? t('common.sending') : t('payment.success.send')}
                            </button>
                            <button
                              className="button button--ghost"
                              onClick={() => {
                                setShowEmailInput(false);
                                setEmail('');
                                setEmailError(null);
                              }}
                              disabled={emailSending}
                              style={{
                                fontSize: '0.9rem',
                                padding: '0.6rem 0.9rem',
                              }}
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
              {roomName && (
                <button
                  className="button"
                  onClick={handleGoToRoom}
                  style={{
                    fontSize: '1rem',
                    padding: '0.85rem 1.5rem',
                    fontWeight: 'bold',
                  }}
                >
                  {t('payment.success.goToRoom')}
                </button>
              )}
              <button
                className="button button--ghost"
                onClick={() => {
                  if (onGoHome) {
                    onGoHome();
                  } else {
                    // Clear URL params and reload to go to home
                    window.history.pushState({}, '', '/');
                    window.location.reload();
                  }
                }}
                style={{
                  fontSize: '0.9rem',
                  padding: '0.75rem 1.25rem',
                }}
              >
                {t('payment.success.goHome')}
              </button>
            </div>
          </div>

          {/* Right Column - Premium Benefits */}
          <div style={{ 
            gridColumn: 'span 1',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
          }}>
            <div className="payment-success-benefits">
              <p className="payment-success-benefits-title">{t('payment.success.benefitsTitle')}</p>
              <ul style={{ textAlign: 'left', margin: 0, paddingLeft: '1.25rem', lineHeight: '1.6' }}>
                <li>{t('payment.success.benefits.allGames')}</li>
                <li>{t('payment.success.benefits.customRoomNames')}</li>
                <li>{t('payment.success.benefits.reservedRoom')}</li>
                <li>{t('payment.success.benefits.statistics')}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </PaymentScreenWrapper>
  );
};

export default PaymentSuccess;
